import React, { useEffect, useState } from 'react';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import WarningConfirmModal from '../../../components/ui/WarningConfirmModal';
import TypeAhead from '../../../components/TypeAhead';
import BillToLookupField, { type BillToValue } from '../../../components/BillToLookupField';
import { getCustomers, type LookupOption } from '../../../services/orderLookups.service';
import { ORDER_MOVE_TYPES } from '../../../constants/orderFieldOptions';
import ordersService from '../../../services/orders.service';
import { validateContainerNumber } from '../../../utils/containerValidator';
import { proDetailPath } from '../../../utils/proSearch';
import { useCanCreateWithoutTemplate } from '../useCanCreateOrder';
import type { CreateOrderModalFields } from './buildCreateOrderSeed';

/* "Create New Order" dialog — V1's new-order launch form (lib_editrecord.pl
 * §2.3/§9.8). Mechanics were copied verbatim from the retired CreateTemplateModal (full reset
 * on every open, `useModalDrag` above the early return, `ModalHeader` +
 * backdrop target-equality close, shared input/label classes + red `*`
 * required markers). Unlike the template modal, THIS modal also owns the
 * container/BOL duplicate check + confirmation dialog before handing off to
 * the caller — the caller (`CreateOrderFlowContext`) only sees the final
 * `onContinue(fields, { bypassDupeCheck })`. */

const EMPTY_BILLTO: BillToValue = { recno: 0, name: '' };

interface Props {
  open: boolean;
  onClose: () => void;
  onContinue: (fields: CreateOrderModalFields, opts: { bypassDupeCheck: boolean }) => void;
  /** Which record the flow is creating (#33471). A template is a blueprint: it
   *  carries no Bkg/BOL and no Equipment No, and is never created FROM another
   *  template — so those three inputs, the container dupe check they drive, and
   *  the template picker are all order-only. Defaults to 'order'. */
  kind?: 'order' | 'template';
}

const CreateOrderModal: React.FC<Props> = ({ open, onClose, onContinue, kind = 'order' }) => {
  const isTemplate = kind === 'template';
  const canUseWithoutTemplate = useCanCreateWithoutTemplate();

  const [movetype, setMovetype] = useState('');
  const [bol, setBol] = useState('');
  const [container, setContainer] = useState('');
  const [containerWarning, setContainerWarning] = useState<string | null>(null);
  const [billTo, setBillTo] = useState<BillToValue>(EMPTY_BILLTO);
  const [customerRecno, setCustomerRecno] = useState(0);
  const [customers, setCustomers] = useState<LookupOption[]>([]);
  // Defaults to 'Y' pre-selected — a DELIBERATE deviation from V1, which
  // treated a blank "Use Template" as silently meaning 'N' (v1-report
  // §2.3/§9.8 foot-gun). Most orders are template-created; forcing an
  // explicit choice or defaulting to the skip-template path would surprise
  // users far more often than this does.
  const [useTemplate, setUseTemplate] = useState<'Y' | 'N'>('Y');
  const [delivertofilter, setDelivertofilter] = useState('');

  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dupes, setDupes] = useState<Array<string | number> | null>(null);

  // Fresh dialog on every open — a create form must never inherit the
  // previous attempt's fields, warnings, or error banner.
  useEffect(() => {
    if (!open) return;
    setMovetype('');
    setBol('');
    setContainer('');
    setContainerWarning(null);
    setBillTo(EMPTY_BILLTO);
    setCustomerRecno(0);
    setUseTemplate('Y');
    setDelivertofilter('');
    setChecking(false);
    setError(null);
    setDupes(null);
  }, [open]);

  // Customer list is session-cached in the service; fetch on first open only.
  useEffect(() => {
    if (!open || customers.length > 0) return;
    let alive = true;
    void getCustomers()
      .then((opts) => { if (alive) setCustomers(opts); })
      .catch(() => { /* the picker just stays empty — non-fatal */ });
    return () => { alive = false; };
  }, [open, customers.length]);

  // Hooks must run on every render — this sits ABOVE the early return (React #310).
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  // Bkg/BOL is required for an order (V1) but does not exist on a template.
  const canContinue =
    billTo.recno > 0 && customerRecno > 0 && !checking
    && (isTemplate || bol.trim() !== '');
  // A dupe check in flight owns the dialog — same "no exit until it settles"
  // rule the retired CreateTemplateModal applied to its create POST.
  const canClose = !checking;

  const handleEquipmentBlur = () => {
    const upper = container.trim().toUpperCase();
    setContainer(upper);
    if (!upper) { setContainerWarning(null); return; }
    // Advisory only — a failed check digit NEVER blocks Continue (V1 parity;
    // legacy container numbers routinely fail the modern ISO 6346 check).
    const result = validateContainerNumber(upper, { strict: false });
    setContainerWarning(result.valid ? null : result.error ?? 'Container number check digit does not match.');
  };

  const buildFields = (): CreateOrderModalFields => ({
    movetype,
    bol,
    container,
    billTo,
    customer: { recno: customerRecno, name: customers.find((o) => o.value === String(customerRecno))?.label ?? '' },
    // Never from a template in template mode — the picker step is unreachable.
    useTemplate: isTemplate ? 'N' : (canUseWithoutTemplate ? useTemplate : 'Y'),
    delivertofilter,
  });

  const handleContinue = async () => {
    if (!canContinue) return;
    setError(null);
    const fields = buildFields();

    // V1 lib_editrecord.pl:19549 — the dupe check only runs when Equipment No
    // is non-blank.
    if (fields.container.trim() === '') {
      onContinue(fields, { bypassDupeCheck: false });
      return;
    }

    setChecking(true);
    try {
      const res = await ordersService.checkDuplicates({ container: fields.container, bol: fields.bol });
      if (res.dupes && res.dupes.length > 0) {
        setDupes(res.dupes);
      } else {
        onContinue(fields, { bypassDupeCheck: false });
      }
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const msg = typeof detail === 'string' && detail
        ? detail
        : e instanceof Error ? e.message : 'Could not check for duplicate orders.';
      setError(msg);
    } finally {
      setChecking(false);
    }
  };

  const inputCls = 'border border-slate-300 dark:border-slate-600 rounded px-1.5 py-1 text-[0.75rem] bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100';
  const labelCls = 'block text-sm font-medium text-label mb-1';
  const req = <span className="text-red-500"> *</span>;

  return (
    <>
      <div
        data-testid="create-order-backdrop"
        className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/45 backdrop-blur-sm p-4 overflow-y-auto"
        onMouseDown={(e) => { if (canClose && e.target === e.currentTarget) onClose(); }}
      >
        <div
          ref={panelRef}
          style={panelStyle}
          role="dialog"
          aria-label={isTemplate ? 'Create New Template' : 'Create New Order'}
          className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-md border border-slate-200 dark:border-slate-700"
        >
          <ModalHeader
            title={isTemplate ? 'Create New Template' : 'Create New Order'}
            onClose={canClose ? onClose : undefined}
            dragHandleProps={dragHandleProps}
          />
          <div className="px-4 py-3 flex flex-col gap-3">
            <div>
              <label className={labelCls} htmlFor="create-order-movetype">Move Type</label>
              <select
                id="create-order-movetype"
                aria-label="Move Type"
                className={`${inputCls} w-full`}
                value={movetype}
                disabled={checking}
                onChange={(e) => setMovetype(e.target.value)}
              >
                <option value="">— optional —</option>
                {ORDER_MOVE_TYPES.map((mt) => <option key={mt} value={mt}>{mt}</option>)}
              </select>
            </div>

            {!isTemplate && (
            <div>
              <label className={labelCls} htmlFor="create-order-bol">Bkg/BOL/Ref No{req}</label>
              <input
                id="create-order-bol"
                aria-label="Bkg/BOL/Ref No"
                type="text"
                className={`${inputCls} w-full`}
                value={bol}
                disabled={checking}
                onChange={(e) => setBol(e.target.value)}
              />
            </div>
            )}

            {!isTemplate && (
            <div>
              <label className={labelCls} htmlFor="create-order-equipment">Equipment No</label>
              <input
                id="create-order-equipment"
                aria-label="Equipment No"
                type="text"
                className={`${inputCls} w-full`}
                value={container}
                disabled={checking}
                onChange={(e) => setContainer(e.target.value)}
                onBlur={handleEquipmentBlur}
              />
              {containerWarning && (
                <p className="mt-1 text-[0.6875rem] text-amber-600 dark:text-amber-400">{containerWarning}</p>
              )}
            </div>
            )}

            <BillToLookupField value={billTo} onChange={setBillTo} disabled={checking} required />

            <div>
              <span className={labelCls}>Customer{req}</span>
              <TypeAhead
                options={customers}
                value={customerRecno ? String(customerRecno) : ''}
                onChange={(next) => setCustomerRecno(next ? Number(next) : 0)}
                placeholder="Search customer…"
                disabled={checking}
                showSearch
                showDescriptions
                compact
                ariaLabel="Customer"
              />
            </div>

            {canUseWithoutTemplate && !isTemplate && (
              <div>
                <label className={labelCls} htmlFor="create-order-use-template">Use Template</label>
                <select
                  id="create-order-use-template"
                  aria-label="Use Template"
                  className={`${inputCls} w-full`}
                  value={useTemplate}
                  disabled={checking}
                  onChange={(e) => setUseTemplate(e.target.value as 'Y' | 'N')}
                >
                  <option value="Y">Y</option>
                  <option value="N">N</option>
                </select>
              </div>
            )}

            {!isTemplate && (
            <div>
              <label className={labelCls} htmlFor="create-order-deliverto-filter">Deliver To Filter</label>
              <input
                id="create-order-deliverto-filter"
                aria-label="Deliver To Filter"
                type="text"
                className={`${inputCls} w-full`}
                value={delivertofilter}
                disabled={checking}
                onChange={(e) => setDelivertofilter(e.target.value)}
                onBlur={(e) => setDelivertofilter(e.target.value.trim().toUpperCase())}
              />
            </div>
            )}

            {error && (
              <div role="alert" className="text-[0.75rem] text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 rounded px-2 py-1.5">
                {error}
              </div>
            )}
          </div>
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={!canClose}
              className="px-3 py-1.5 text-[0.78125rem] font-medium text-slate-700 bg-white border border-slate-300 rounded dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleContinue()}
              disabled={!canContinue}
              className="px-4 py-1.5 text-[0.78125rem] font-semibold text-white bg-blue-600 border border-blue-700 rounded hover:bg-blue-700 disabled:opacity-40"
            >
              {checking ? 'Checking…' : 'Continue'}
            </button>
          </div>
        </div>
      </div>

      {dupes && (
        <WarningConfirmModal
          open
          title="Duplicate Orders Found"
          message={
            <>
              The container / BOL is already on{' '}
              {dupes.length === 1 ? 'order' : 'orders'}{' '}
              {dupes.map((d, i) => (
                <React.Fragment key={String(d)}>
                  {i > 0 && ', '}
                  <a
                    href={proDetailPath(d)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {d}
                  </a>
                </React.Fragment>
              ))}
              . Open {dupes.length === 1 ? 'it' : 'them'} in a new tab to
              compare, then save this order anyway?
            </>
          }
          confirmLabel="Save Order"
          cancelLabel="Back"
          onConfirm={() => {
            onContinue(buildFields(), { bypassDupeCheck: true });
            setDupes(null);
          }}
          onCancel={() => setDupes(null)}
        />
      )}
    </>
  );
};

export default CreateOrderModal;
